/** @jsxImportSource @emotion/react */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { css } from "@emotion/react";
import {
  Button,
  Card,
  Column,
  ExternalLink,
  Icon,
  IconNames,
  Input,
  Row,
  Text,
  TEXT_VARIANTS,
} from "../components";
import { COLORS } from "../shared/theme";
import axios from "axios";
import isURL from "validator/lib/isURL";
import { useNavigate } from "react-router-dom";
import {getAuth} from "firebase/auth";

const Textarea: React.FC<React.ComponentPropsWithoutRef<"textarea">> = (
  props
) => {
  const root = useRef<HTMLTextAreaElement | null>(null);
  const offset = useRef<number>(0);

  useLayoutEffect(() => {
    if (root.current === null) return;
    offset.current = root.current.offsetHeight - root.current.clientHeight;
    (root.current as HTMLTextAreaElement).style.height = "auto";
    (root.current as HTMLTextAreaElement).style.height = `${
      (root.current as HTMLTextAreaElement).scrollHeight + offset.current
    }px`;
  }, [root]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const { target } = e;

      (target as HTMLTextAreaElement).style.height = "auto";
      (target as HTMLTextAreaElement).style.height = `${
        (target as HTMLTextAreaElement).scrollHeight + offset.current
      }px`;
    },
    [offset]
  );

  return (
    <textarea
      ref={root}
      rows={1}
      onInput={handleKeyDown}
      css={css`
        resize: none;
        height: 45px;
        padding: 8px 10px;
        overflow: hidden;
        font: 400 20px/25px SF Pro;
        border-radius: 10px;
        border: 2px solid ${COLORS.BORDER};
        color: ${COLORS.BLACK};

        &::placeholder {
          font-weight: 400;
          color: ${COLORS.SILVER};
        }
        &:focus {
          border-color: ${COLORS.BLACK};
          outline: none;
        }
      `}
      {...props}
    />
  );
};

type TPreview = {
  description: string;
  domain: string;
  thumbnail: string;
  title: string;
  type: "link" | "article" | "audio";
  url: string;
};

type TPreviewResponse = TPreview;

const Toggle: React.FC<{ checked?: boolean; onChange?: () => void }> = ({
  checked,
  onChange,
}) => <input type="checkbox" checked={checked} onChange={onChange} />;

const Add: React.FC = () => {
  const [preview, setPreview] = useState<TPreview | null>(null);
  const [requestedTypeDetection, setRequestedTypeDetection] = useState(false);
  const [query, setQuery] = useState("");
  const [text, setText] = useState(query);
  const [focus, setFocus] = useState<"author" | null>(null);
  const [authorName, setAuthorName] = useState("");
  const [requestedAuthorCreation, setRequestedAuthorCreation] = useState(false);
  const [createdAuthors, setCreatedAuthors] = useState<Array<string>>([]);
  const [isFocused, setIsFocused] = useState(false);
  const navigate = useNavigate();

  const add = useCallback(async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    try {
      const token = await user?.getIdToken();
      await axios.post("https://staging.sublime.app/api/v2/entities/add", {
        entity_type: "curation.text",
        text,
        html: text,
        connections: [],
        mark_as_favorite: false,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      navigate("/");
    } catch (error) {
      console.error(error);
    }
  }, [navigate, text]);

  useEffect(() => {
    if (requestedTypeDetection && isURL(query)) {
      const requestTypeFromEngine = async () => {
        try {
          const { data } = await axios.get<TPreviewResponse>(
            "https://develop-detection-engine.sublime.app/preview/",
            {
              params: {
                url: query,
              },
            }
          );
          setPreview(data);
        } catch (e) {
          console.error(e);
        }
      };

      requestTypeFromEngine();
    }
  }, [query, requestedTypeDetection]);

  return (
    <Row mainAxisAlignment="center">
      <Column
        gap={10}
        css={css`
          width: 766px;
          padding-bottom: 50px;
        `}
      >
        {!requestedTypeDetection ? (
          <div
            css={css`
              position: relative;
            `}
          >
            {!isFocused && query.length === 0 && (
              <Column
                crossAxisAlignment="center"
                css={css`
                  pointer-events: none;
                  transform: translate(-50%, -50%);
                  position: absolute;
                  top: 50%;
                  left: 50%;
                `}
              >
                <Text
                  variant={TEXT_VARIANTS.T25}
                  css={css`
                    color: ${COLORS.NUMBER};
                  `}
                >
                  {"Paste a link, type text,"}
                </Text>
                <Text
                  variant={TEXT_VARIANTS.T25}
                  css={css`
                    color: ${COLORS.NUMBER};
                  `}
                >
                  {"drop or "}
                  <Text
                    as="span"
                    variant={TEXT_VARIANTS.T25}
                    css={css`
                      cursor: pointer;
                      pointer-events: all;
                      text-decoration: underline;
                      text-underline-offset: 4px;
                    `}
                  >
                    {"choose"}
                  </Text>
                  {" image/file"}
                </Text>
              </Column>
            )}
            <Textarea
              rows={3}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setText(e.target.value);
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  setRequestedTypeDetection(true);
                }
              }}
              css={css`
                width: 100%;
                padding: 10px;
                border: 2px solid ${COLORS.BORDER};
                font-size: 20px;
                line-height: 25px;
                border-radius: 10px;
              `}
            />
            {query.length > 0 && (
              <Icon
                name={IconNames.Enter}
                css={css`
                  cursor: pointer;
                  position: absolute;
                  right: 10px;
                  bottom: 10px;
                `}
              />
            )}
          </div>
        ) : !isURL(query) ? (
          <Column gap={50}>
            <Column gap={25}>
              <div
                css={css`
                  position: relative;
                `}
              >
                <Textarea
                  autoFocus
                  rows={3}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onFocus={(e) => {
                    e.target.setSelectionRange(
                      e.target.value.length,
                      e.target.value.length
                    );
                  }}
                  css={css`
                    width: 100%;
                    padding: 10px;
                    border: 2px solid ${COLORS.BORDER};
                    font-size: 20px;
                    line-height: 25px;
                    border-radius: 10px;
                  `}
                />
              </div>
              <Column
                gap={10}
                css={css`
                  transition: opacity 120ms ease;
                  opacity: ${focus === "author" && authorName.length > 0
                    ? 0
                    : 1};
                `}
              >
                <Text
                  variant={TEXT_VARIANTS.T25}
                  css={css`
                    padding: 0 10px;
                    color: ${COLORS.SILVER};
                  `}
                >
                  {"Connections"}
                </Text>
                <Input type="text" placeholder="Connect to collections" />
              </Column>
              <Row
                crossAxisAlignment="center"
                gap={10}
                css={css`
                  padding: 0 10px;
                `}
              >
                <Text
                  variant={TEXT_VARIANTS.T20BOLD}
                  css={css`
                    color: ${COLORS.SILVER};
                  `}
                >
                  {"Mark as favorite"}
                </Text>
                <Toggle />
              </Row>
            </Column>
            <Button
              variant="caps-active"
              size="big"
              label="Add to Sublime"
              textVariant={TEXT_VARIANTS.T25}
              css={css`
                transition: opacity 120ms ease;
                opacity: ${focus === "author" ? 0 : 1};
              `}
              onClick={add}
            />
          </Column>
        ) : preview === null ? (
          <div>{"loading..."}</div>
        ) : (
          <Column gap={50}>
            <Column gap={25}>
              <Column gap={5}>
                <Card
                  css={css`
                    transition: all 120ms ease;
                    cursor: pointer;
                    padding: 10px;
                    border: none;
                    box-shadow: 0 0.1rem 0.5rem rgb(0 0 0 / 10%);

                    &:hover {
                      box-shadow: 0 0.5rem 1.5rem rgb(0 0 0 / 10%);
                    }

                    &:hover .feed-card__footer__connections {
                      color: ${COLORS.GREEN};
                    }
                  `}
                >
                  <Column gap={10}>
                    <img
                      src={preview?.thumbnail}
                      alt=""
                      css={css`
                        object-fit: cover;
                        width: 100%;
                        height: 350px;
                      `}
                    />
                    <Column crossAxisAlignment="start" gap={5}>
                      <Text as="h3" variant={TEXT_VARIANTS.T30}>
                        {preview.title}
                      </Text>
                      <Text as="p" variant={TEXT_VARIANTS.T20}>
                        {preview.description.slice(0, 160).concat("...")}
                      </Text>
                      <ExternalLink>
                        {new URL(preview.url).host.split("www.")}
                      </ExternalLink>
                    </Column>
                  </Column>
                </Card>
                <Column gap={5}>
                  {!requestedAuthorCreation && (
                    <Input
                      type="text"
                      placeholder="Add author or creator"
                      value={authorName}
                      onChange={(e) => setAuthorName(e.target.value)}
                      onFocus={() => setFocus("author")}
                      onBlur={() => setFocus(null)}
                    />
                  )}
                  {authorName.length > 0 && requestedAuthorCreation && (
                    <Column gap={5}>
                      <Row
                        mainAxisAlignment="space-between"
                        css={css`
                          padding: 10px;
                          border: 1px solid ${COLORS.BORDER};
                          border-radius: 10px;
                        `}
                      >
                        <Text variant={TEXT_VARIANTS.T20BOLD}>
                          {authorName}
                        </Text>
                        <Icon
                          name={IconNames.X}
                          css={css`
                            cursor: pointer;
                          `}
                          onClick={() => {
                            setRequestedAuthorCreation(false);
                            setAuthorName("");
                            setFocus(null);
                          }}
                        />
                      </Row>
                      <Input type="text" placeholder="Add a bio" />
                      <Input type="text" placeholder="Add a URL" />
                      <Row
                        role="button"
                        mainAxisAlignment="center"
                        css={css`
                          cursor: pointer;
                          padding: 10px;
                          border: 1px solid ${COLORS.BORDER};
                          border-radius: 10px;
                        `}
                        onClick={() => {
                          setRequestedAuthorCreation(false);
                          setCreatedAuthors(
                            createdAuthors.concat([authorName])
                          );
                          setAuthorName("");
                        }}
                      >
                        <Text
                          variant={TEXT_VARIANTS.T20BOLD}
                          css={css`
                            color: ${COLORS.BLUE};
                          `}
                        >
                          {`Create new person ‘${authorName}’`}
                        </Text>
                      </Row>
                    </Column>
                  )}
                  {focus === "author" &&
                    authorName.length > 0 &&
                    !requestedAuthorCreation && (
                      <Column gap={5}>
                        <Row
                          role="button"
                          css={css`
                            cursor: pointer;
                            padding: 10px;
                            border: 1px solid ${COLORS.BORDER};
                            border-radius: 10px;
                          `}
                          onPointerDown={() => setRequestedAuthorCreation(true)}
                        >
                          <Text
                            variant={TEXT_VARIANTS.T20}
                            css={css`
                              color: ${COLORS.BLUE};
                            `}
                          >
                            {`Create new person ‘${authorName}’`}
                          </Text>
                        </Row>
                        <Row
                          css={css`
                            padding: 10px;
                            border: 1px solid ${COLORS.BORDER};
                            border-radius: 10px;
                          `}
                        >
                          <Column
                            css={css`
                              flex-grow: 1;
                            `}
                          >
                            <Text variant={TEXT_VARIANTS.T20BOLD}>
                              {"John Smith"}
                            </Text>
                            <Text
                              variant={TEXT_VARIANTS.T20}
                              css={css`
                                color: ${COLORS.SILVER};
                              `}
                            >
                              {"person · 88 connections"}
                            </Text>
                            <Text variant={TEXT_VARIANTS.T20}>
                              {"Internet explorer"}
                            </Text>
                            <ExternalLink>{"explorer.com"}</ExternalLink>
                          </Column>
                          <Icon name={IconNames.Preview} />
                        </Row>
                        <Row
                          css={css`
                            padding: 10px;
                            border: 1px solid ${COLORS.BORDER};
                            border-radius: 10px;
                          `}
                        >
                          <Column
                            css={css`
                              flex-grow: 1;
                            `}
                          >
                            <Text variant={TEXT_VARIANTS.T20BOLD}>
                              {"John McLein"}
                            </Text>
                            <Text
                              variant={TEXT_VARIANTS.T20}
                              css={css`
                                color: ${COLORS.SILVER};
                              `}
                            >
                              {"person · 22 connections"}
                            </Text>
                          </Column>
                          <Icon name={IconNames.Preview} />
                        </Row>
                        <Row
                          css={css`
                            padding: 10px;
                            border: 1px solid ${COLORS.BORDER};
                            border-radius: 10px;
                          `}
                        >
                          <Column
                            css={css`
                              flex-grow: 1;
                            `}
                          >
                            <Text variant={TEXT_VARIANTS.T20BOLD}>
                              {"Jonathan Green"}
                            </Text>
                            <Text
                              variant={TEXT_VARIANTS.T20}
                              css={css`
                                color: ${COLORS.SILVER};
                              `}
                            >
                              {"person · 11 connections"}
                            </Text>
                            <Text variant={TEXT_VARIANTS.T20}>
                              {"Producer"}
                            </Text>
                          </Column>
                          <Icon name={IconNames.Preview} />
                        </Row>
                      </Column>
                    )}
                </Column>
              </Column>
              <Column
                gap={10}
                css={css`
                  transition: opacity 120ms ease;
                  opacity: ${focus === "author" && authorName.length > 0
                    ? 0
                    : 1};
                `}
              >
                <Text
                  variant={TEXT_VARIANTS.T25}
                  css={css`
                    padding: 0 10px;
                    color: ${COLORS.SILVER};
                  `}
                >
                  {"Note"}
                </Text>
                <Textarea placeholder="Write a note, make it as long or as short as you want" />
              </Column>
              <Column
                gap={10}
                css={css`
                  transition: opacity 120ms ease;
                  opacity: ${focus === "author" && authorName.length > 0
                    ? 0
                    : 1};
                `}
              >
                <Text
                  variant={TEXT_VARIANTS.T25}
                  css={css`
                    padding: 0 10px;
                    color: ${COLORS.SILVER};
                  `}
                >
                  {"Highlights"}
                </Text>
                <Input type="text" placeholder="Paste a highlight" />
              </Column>
              <Column
                gap={10}
                css={css`
                  transition: opacity 120ms ease;
                  opacity: ${focus === "author" && authorName.length > 0
                    ? 0
                    : 1};
                `}
              >
                <Text
                  variant={TEXT_VARIANTS.T25}
                  css={css`
                    padding: 0 10px;
                    color: ${COLORS.SILVER};
                  `}
                >
                  {"Connections"}
                </Text>
                <Input type="text" placeholder="Connect to collections" />
              </Column>
            </Column>
            <Button
              variant="caps-active"
              size="big"
              label="Add to Sublime"
              textVariant={TEXT_VARIANTS.T25}
              css={css`
                transition: opacity 120ms ease;
                opacity: ${focus === "author" ? 0 : 1};
              `}
            />
          </Column>
        )}
      </Column>
    </Row>
  );
};

export default Add;
